import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import DriverProfileCard from "@/components/DriverProfileCard";
import DocumentationStrengthCard from "@/components/DocumentationStrengthCard";
import RnIntakeCard from "@/components/RnIntakeCard";
import CrashReadyVerifiedCard from "@/components/CrashReadyVerifiedCard";
import api, { setMode } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  AlertTriangle, FolderLock, ShieldCheck, Stethoscope, Scale, Settings, Phone,
  FileText, ArrowRight, Plus, Clock, ChevronRight, PlayCircle,
} from "lucide-react";

const TILES = [
  { key: "cases", label: "My Cases", desc: "View and manage all your reports", icon: FileText, to: "/cases" },
  { key: "vault", label: "Evidence Vault", desc: "All your photos, videos & audio", icon: FolderLock, to: "/evidence" },
  { key: "insurance", label: "Insurance Info", desc: "Track claims & adjusters", icon: ShieldCheck, to: "/dashboard", soon: true },
  { key: "medical", label: "Medical Providers", desc: "Log treatment & providers", icon: Stethoscope, to: "/dashboard", soon: true },
  { key: "attorney", label: "Attorney Messages", desc: "Secure attorney communication", icon: Scale, to: "/dashboard", soon: true },
  { key: "settings", label: "Settings", desc: "Profile & preferences", icon: Settings, to: "/profile" },
];

const STATUS_COLOR = {
  draft: "bg-amber-50 text-amber-600",
  active: "bg-green-50 text-green-600",
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const welcome = params.get("welcome") === "1";
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({ cases: 0, evidence: 0, active: 0, referrals: 0 });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([api.get("/cases"), api.get("/stats")]);
      setCases(c.data);
      setStats(s.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const startAccident = async () => {
    setMode("LIVE");
    setCreating(true);
    try {
      const { data } = await api.post("/cases", { practice_area: "auto_accident", mode: "LIVE" });
      navigate(`/new-accident?case=${data.case_id}`);
    } catch {
      toast.error("Could not start a new report");
      setCreating(false);
    }
  };

  const startDemo = () => {
    sessionStorage.setItem("cr_demo_source", "dashboard");
    setMode("DEMO");
    navigate("/demo");
  };

  // Arriving from "EXIT DEMO AND START LIVE ACCIDENT" — begin the live flow immediately.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (params.get("live") === "1" && !autoStarted.current) {
      autoStarted.current = true;
      startAccident();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AppLayout>
      <div className="cr-fade-up">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-widest">Dashboard</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-navy mt-1">
          Hello, {user?.name?.split(" ")[0] || "there"}.
        </h1>
        <p className="mt-2 text-slate-600">Stay calm. We'll walk you through everything, step by step.</p>
      </div>

      {/* Emergency CTA — LIVE ACCIDENT */}
      <button onClick={startAccident} disabled={creating} data-testid="start-accident-btn"
        className="mt-8 w-full group bg-red-600 hover:bg-red-700 text-white rounded-3xl p-6 md:p-8 flex items-center justify-between text-left hover:-translate-y-0.5 transition-all shadow-[0_12px_40px_rgb(220,38,38,0.35)] active:scale-[0.99] disabled:opacity-70">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-white/15 flex items-center justify-center">
            <AlertTriangle className="w-7 h-7 text-white" />
          </div>
          <div>
            <div className="font-heading text-xl md:text-2xl font-extrabold tracking-wide">{creating ? "Starting…" : "LIVE ACCIDENT"}</div>
            <div className="text-red-100 text-sm mt-0.5">Only press this button if you are currently involved in an actual motor vehicle accident.</div>
          </div>
        </div>
        <ArrowRight className="w-6 h-6 text-white/70 group-hover:translate-x-1 transition-transform" />
      </button>

      {/* DEMO mode */}
      <button onClick={startDemo} data-testid="start-demo-btn"
        className="mt-4 w-full group bg-white border-2 border-dashed border-slate-200 rounded-3xl p-5 md:p-6 flex items-center justify-between text-left hover:border-cobalt hover:-translate-y-0.5 transition-all active:scale-[0.99]">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-2xl bg-cobalt-light flex items-center justify-center">
            <PlayCircle className="w-6 h-6 text-cobalt" />
          </div>
          <div>
            <div className="font-heading text-lg font-extrabold tracking-wide text-navy">DEMO</div>
            <div className="text-slate-500 text-sm mt-0.5">Want to explore our features?</div>
            <div className="text-slate-400 text-xs mt-0.5">Experience Crash Ready without creating a real accident report or contacting anyone.</div>
          </div>
        </div>
        <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-cobalt group-hover:translate-x-1 transition-all" />
      </button>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total Cases", value: stats.cases },
          { label: "Active", value: stats.active },
          { label: "Evidence Files", value: stats.evidence },
          { label: "Referrals", value: stats.referrals },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl border border-slate-100 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="font-heading text-3xl font-extrabold text-navy">{s.value}</div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Driver Profile */}
      <DriverProfileCard welcome={welcome} />

      {/* Documentation Strength */}
      <DocumentationStrengthCard cases={cases} />

      {/* RN Intake (Phase 2) */}
      <RnIntakeCard cases={cases} />
      <CrashReadyVerifiedCard caseId={(cases.find((c) => c.rn_case_decision === "case_verified") || cases.find((c) => c.status === "active" && c.verification) || cases.find((c) => c.verification))?.case_id} />

      {/* Bento tiles */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {TILES.map(({ key, label, desc, icon: Icon, to, soon }) => (
          <Link key={key} to={to} data-testid={`tile-${key}`}
            className="group bg-white rounded-3xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-0.5 hover:shadow-lg transition-all relative">
            {soon && <span className="absolute top-4 right-4 text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-400 px-2 py-1 rounded-full">Soon</span>}
            <div className="h-12 w-12 rounded-2xl bg-cobalt-light flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-cobalt" /></div>
            <h3 className="font-heading font-semibold text-navy flex items-center gap-1">{label}</h3>
            <p className="mt-1 text-sm text-slate-500">{desc}</p>
          </Link>
        ))}
      </div>

      {/* Recent cases */}
      <div className="mt-10">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold text-navy">My Cases</h2>
          <button onClick={startAccident} className="flex items-center gap-1.5 text-sm font-medium text-cobalt"><Plus className="w-4 h-4" /> New</button>
        </div>
        <div className="mt-4 space-y-3">
          {cases.length === 0 ? (
            <div className="bg-white rounded-3xl border border-dashed border-slate-200 p-10 text-center">
              <FileText className="w-10 h-10 text-slate-300 mx-auto" />
              <p className="mt-3 text-slate-500">No cases yet. Start your first report above.</p>
            </div>
          ) : cases.map((c) => (
            <Link key={c.case_id} to={`/cases/${c.case_id}`} data-testid={`case-row-${c.case_id}`}
              className="bg-white rounded-2xl border border-slate-100 p-5 flex items-center justify-between hover:-translate-y-0.5 hover:shadow-md transition-all">
              <div className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-xl bg-cobalt-light flex items-center justify-center"><FileText className="w-5 h-5 text-cobalt" /></div>
                <div>
                  <div className="font-semibold text-navy">{c.title}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                    <Clock className="w-3 h-3" /> {new Date(c.created_at).toLocaleDateString()} · {c.evidence_count} files
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_COLOR[c.status] || "bg-slate-100 text-slate-500"}`}>{c.status}</span>
                <ChevronRight className="w-5 h-5 text-slate-300" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      <a href="tel:911" data-testid="call-911-floating" className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-5 py-3.5 rounded-full shadow-xl font-semibold transition-all active:scale-95">
        <Phone className="w-5 h-5" /> Call 911
      </a>
    </AppLayout>
  );
}
